/**
 * Compresse une image en JPEG avec une qualité de 70%
 * @param file - Le fichier image à compresser
 * @param quality - La qualité JPEG (0.7 par défaut)
 * @param maxWidth - Largeur maximale (800px par défaut)
 * @param maxHeight - Hauteur maximale (800px par défaut)
 * @returns Promise<File> - Le fichier compressé
 */
export function compressImage(file: File, quality: number = 0.7, maxWidth: number = 800, maxHeight: number = 800): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    if (!ctx) {
      reject(new Error("Impossible de créer le contexte canvas"));
      return;
    }

    img.onload = () => {
      // Calculer les nouvelles dimensions en conservant le ratio
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, width, height);

      // Convertir en blob JPEG avec la qualité spécifiée
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Erreur lors de la compression de l'image"));
            return;
          }

          // Créer un nouveau fichier avec le blob compressé
          const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
            type: "image/jpeg",
            lastModified: Date.now(),
          });

          resolve(compressedFile);
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      reject(new Error("Erreur lors du chargement de l'image"));
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Valide qu'un fichier est une image supportée
 * @param file - Le fichier à valider
 * @returns boolean
 */
export function isValidImageFile(file: File): boolean {
  const supportedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  return supportedTypes.includes(file.type);
}

/**
 * Formate la taille d'un fichier en format lisible
 * @param bytes - Taille en bytes
 * @returns string - Taille formatée
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}
